import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldWarning, CheckCircle, XCircle, Warning, Notebook, Clock, MapPin, User as UserIcon } from "@phosphor-icons/react";

/**
 * Iter112: Pantalla pública de verificación de bitácora — sin login.
 * Se accede escaneando el QR del PDF (o ingresando manualmente el token OID)
 * y muestra al auditor un resumen firmado + estado de integridad SHA-256.
 */
export default function BitacoraVerify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    axios
      .get(`${base}/api/bitacora/public/verify/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Token no encontrado o inválido"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500" data-testid="bitverify-loading">Validando token…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-lg p-6 text-center" data-testid="bitverify-error">
          <XCircle size={40} weight="bold" className="mx-auto text-red-600 mb-2" />
          <p className="font-heading font-black text-slate-900 text-lg mb-1">Token inválido</p>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4 font-mono break-all">{token}</p>
        </div>
      </div>
    );
  }

  const sigs = data.signatures || {};
  const okSig = Object.values(sigs).filter((s) => s.signed).length;
  const totSig = Object.keys(sigs).length;
  const integrity = data.integrity_ok;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="bitverify-header">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-12 w-12 rounded-lg bg-blue-900 flex items-center justify-center shrink-0">
              <Notebook size={26} weight="fill" className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Mafrissa UG · Verificación pública</p>
              <p className="font-heading font-black text-slate-900 text-2xl">Bitácora de operaciones</p>
              <p className="text-xs text-slate-500 mt-1 font-mono break-all">{data.audit_token}</p>
            </div>
          </div>

          {/* Integrity banner */}
          <div
            className={`rounded-lg p-4 flex items-center gap-3 ${
              integrity ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"
            }`}
            data-testid="bitverify-integrity"
          >
            {integrity ? (
              <ShieldCheck size={32} weight="fill" className="text-emerald-600 shrink-0" />
            ) : (
              <ShieldWarning size={32} weight="fill" className="text-red-600 shrink-0" />
            )}
            <div className="flex-1">
              <p className={`font-heading font-black ${integrity ? "text-emerald-800" : "text-red-800"}`}>
                {integrity ? "Documento auténtico" : "Integridad comprometida"}
              </p>
              <p className={`text-xs ${integrity ? "text-emerald-700" : "text-red-700"}`}>
                {integrity
                  ? "El hash SHA-256 registrado coincide con el contenido actual. La bitácora no ha sido alterada desde su firma."
                  : "El hash registrado NO coincide con el contenido actual. La bitácora fue modificada después de su registro."}
              </p>
              <p className="text-[10px] text-slate-500 mt-1 font-mono">
                Hash registrado: <b>{data.hash_stored}</b> · Hash actual: <b>{data.hash_current}</b>
              </p>
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="bitverify-meta">
          <p className="overline-label mb-3">Datos del turno</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1"><Clock size={10} /> Fecha</p>
              <p className="font-heading font-bold text-slate-900">{data.date || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-bold">Turno</p>
              <p className="font-heading font-bold text-slate-900 capitalize">{data.shift || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1"><MapPin size={10} /> Área</p>
              <p className="font-heading font-bold text-slate-900">{data.area || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1"><UserIcon size={10} /> Supervisor</p>
              <p className="font-heading font-bold text-slate-900">{data.supervisor_name || "—"}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-4 gap-2 text-center">
            <MetricBox label="Operaciones" value={data.operations_count} />
            <MetricBox label="Materiales" value={data.materials_count} />
            <MetricBox label="Fotos" value={data.photos_count} />
            <MetricBox label="Estado" value={data.status || "—"} highlight={data.status === "closed"} />
          </div>
        </div>

        {/* Signatures */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="bitverify-signatures">
          <div className="flex items-center justify-between mb-4">
            <p className="overline-label">Firmas de conformidad</p>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${okSig === totSig ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {okSig} / {totSig} firmadas
            </span>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {Object.entries(sigs).map(([key, s]) => (
              <div
                key={key}
                className={`rounded-lg border p-3 ${s.signed ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
                data-testid={`bitverify-sig-${key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {s.signed ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                  ) : (
                    <Warning size={16} weight="fill" className="text-amber-500" />
                  )}
                  <span className={`text-[10px] font-bold uppercase ${s.signed ? "text-emerald-800" : "text-amber-700"}`}>
                    {s.signed ? "Firmada" : "Pendiente"}
                  </span>
                </div>
                <p className="font-heading font-bold text-slate-900 text-sm">{s.name || "—"}</p>
                <p className="text-[10px] text-slate-500 mt-1">{s.role}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Actividades + Plan */}
        {(data.activities_done || data.plan_next) && (
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="bitverify-narrative">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="overline-label mb-2">Actividades realizadas</p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{data.activities_done || "—"}</p>
              </div>
              <div>
                <p className="overline-label mb-2">Plan próximo turno</p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{data.plan_next || "—"}</p>
              </div>
            </div>
            {(data.resources_needed || []).length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="overline-label mb-2">Recursos necesarios ({data.resources_needed.length})</p>
                <ul className="text-sm text-slate-700 space-y-0.5">
                  {data.resources_needed.map((r, i) => <li key={i}>• {r}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-[10px] text-slate-400 mt-6">
          Verificación pública emitida por Mafrissa UG · Sistema de Gestión Minera
        </p>
      </div>
    </div>
  );
}

function MetricBox({ label, value, highlight = false }) {
  return (
    <div className={`rounded p-2 border ${highlight ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
      <p className="text-[9px] uppercase text-slate-500 font-bold">{label}</p>
      <p className={`font-heading font-black text-sm ${highlight ? "text-emerald-800" : "text-slate-900"}`}>{value ?? "—"}</p>
    </div>
  );
}
